import type { LocalStorageKeyOptions, SessionStorageKeyOptions } from './constants/storage';

export function checkWebStorage<T>(cb?: () => T): T | null {
  try {
    if (typeof window === 'undefined') {
      throw new Error('Web storage is only available in the browser');
    }
    if (!cb) {
      throw new Error('Callback is required');
    }
    return cb();
  } catch (error) {
    console.error('Error checking web storage', error);
    return null;
  }
}

export const storage = {
  local: {
    get: function localStorageGet<T>(key: LocalStorageKeyOptions, options: { parse?: boolean } = { parse: true }) {
      return checkWebStorage(() => {
        const item = localStorage.getItem(key);
        if (item) {
          try {
            if (options?.parse === false) {
              return item as T;
            }
            return JSON.parse(item) as T;
          } catch (error) {
            if (typeof item === 'string' && item.length > 0) {
              console.error('Error parsing local storage item', error);
              return item as T;
            }
            return null;
          }
        }
      });
    },
    set: function localStorageSet<T>(key: LocalStorageKeyOptions, value: T) {
      return checkWebStorage(() => localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value)));
    },
    remove: function localStorageRemove(key: LocalStorageKeyOptions) {
      return checkWebStorage(() => localStorage.removeItem(key));
    },
  },
  session: {
    get: function sessionStorageGet<T>(key: SessionStorageKeyOptions, options: { parse?: boolean } = { parse: true }) {
      return checkWebStorage(() => {
        const item = sessionStorage.getItem(key);
        if (item) {
          try {
            if (options?.parse === false) {
              return item as T;
            }
            return JSON.parse(item) as T;
          } catch (error) {
            if (typeof item === 'string' && item.length > 0) {
              console.error('Error parsing session storage item', error);
              return item as T;
            }
            return null;
          }
        }
        return null;
      });
    },
    set: function sessionStorageSet<T>(key: SessionStorageKeyOptions, value: T) {
      return checkWebStorage(() => sessionStorage.setItem(key, JSON.stringify(value)));
    },
    remove: function sessionStorageRemove(key: SessionStorageKeyOptions) {
      return checkWebStorage(() => sessionStorage.removeItem(key));
    },
  },
};
